import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import OnboardingWizard from "./referralOnboarding/OnboardingWizard";
import {
  buildCompletePayload,
  completeOnboarding,
  fetchOnboardingPreview,
  uploadOnboardingDocument,
} from "./referralOnboardingApi";
import { describeInvitationError } from "./invitationUnavailable";

const PREVIEW_INVITE = {
  name: "Jane Smith",
  email: "jane.partner@example.com",
  phone: "+61 400 000 000",
  organisationName: "Kothar Education",
  termsVersion: "2026.1",
  termsHtml: null,
};

const fromPreview = (data) => ({
  organisationName: data.organisationName,
  termsVersion: data.termsVersion,
  termsHtml: data.termsHtml,
  form: { fullName: data.name || "", email: data.email || "", phone: data.phone || "" },
  lockedFields: ["email"],
  documentsOnFile: [],
});

const inviteAdapter = (token) => ({
  requirePassword: true,
  lockedHint: "Fixed by your invitation",
  load: () =>
    token
      ? fetchOnboardingPreview(token).then(fromPreview)
      : Promise.reject(new Error("This invitation link is missing a token.")),
  upload: (kind, file) => uploadOnboardingDocument(token, kind, file),
  complete: (form, termsVersion) => completeOnboarding(token, buildCompletePayload(form, termsVersion)),
  describeError: describeInvitationError,
});

const previewAdapter = {
  preview: true,
  requirePassword: true,
  lockedHint: null,
  load: () => Promise.resolve({ ...fromPreview(PREVIEW_INVITE), lockedFields: [] }),
  upload: () => Promise.resolve(),
  complete: () => new Promise((resolve) => setTimeout(() => resolve({ preview: true }), 600)),
};

const Done = ({ isPreview }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
    <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
      <svg className="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h2 className="text-xl font-bold text-slate-900 mb-2">
      {isPreview ? "Preview complete" : "Your account is active"}
    </h2>
    <p className="text-slate-500 m-0">
      {isPreview
        ? "This was a UI preview only. Real invites open the portal as soon as onboarding completes."
        : "Thanks, your partner account is ready."}
    </p>
    {!isPreview ? (
      <Link
        to="/login"
        className="inline-block mt-5 rounded-lg bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-teal-700"
      >
        Sign in to your portal
      </Link>
    ) : null}
  </div>
);

/**
 * Public onboarding for invited referral partners.
 * Preview: /referral/onboarding/preview
 */
const ReferralOnboardingPage = () => {
  const { token } = useParams();
  const isPreview = token === "preview";
  const adapter = useMemo(() => (isPreview ? previewAdapter : inviteAdapter(token)), [isPreview, token]);

  return (
    <OnboardingWizard
      adapter={adapter}
      copy={{
        headline: (org) => `Join ${org}`,
        intro:
          "A few short steps to set up your partner profile. Completing them opens your referral portal straight away.",
        failureTitle: "Invitation unavailable",
        submitLabel: "Create my account",
        notice: isPreview ? "Preview mode: nothing is saved to the server." : null,
      }}
      onDone={() =>
        toast.success(
          isPreview ? "Preview only, nothing was saved" : "Your partner account is active. You can now sign in"
        )
      }
      renderDone={() => <Done isPreview={isPreview} />}
    />
  );
};

export default ReferralOnboardingPage;
